import { type JSX, useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '../primitives';
import { DataPill } from '../status-pill';
import { TableFilter } from '../table-chrome';
import {
  formatNextEvent,
  formatOffset,
  formatPlatformTime,
  orderHandle,
  PLAY_INTERVAL_MS,
  POLL_INTERVAL_MS,
  playContinues,
  type RehearsalSnapshot,
  roboBakerPresses,
  scenarioTargets,
} from './state';

/**
 * The rehearsal bar (PRD TST-003): the platform's clock and the platform's
 * morning, on a strip at the bottom of every portal.
 *
 * A rehearsal happens in four browser tabs on four different origins, and until
 * this existed the only way to move any of them was curl. The strip is the same
 * in all four because the thing it drives is the same in all four - there is
 * exactly one rehearsal clock per runtime (`apps/api-worker/src/rehearsal-clock.ts`),
 * and the tabs share nothing else. Press `Next morning` in the guest tab and the
 * counter tablet's board fills, because the SERVER moved, not the page.
 *
 * ## Why it is safe to mount unconditionally
 *
 * It renders nothing until `GET /dev/rehearsal` has answered with JSON, and it
 * stops asking for good the moment that answer is a 404 - which is what
 * production is, because `devAuthEnabled` gates every route it calls. The apps
 * additionally mount it behind `import.meta.env.DEV`, so a production BUILD does
 * not carry it at all; the 404 guard is the second lock, for a dev build pointed
 * at a deployed environment.
 *
 * ## Why its words are not translated
 *
 * Every user-visible string in this package is a required, already-translated
 * prop (NFR-006), and this component deliberately breaks that rule. The reader
 * here is the founder or an engineer driving a rehearsal, never a guest and
 * never a baker; wiring ten labels through four apps' catalogues in two
 * languages would be real work with nobody on the other end of it. If this
 * strip ever becomes something a partner sees, that is the moment the labels
 * become props.
 */
/**
 * The custom property the strip publishes its own height in, on the root
 * element, for as long as it is on screen. `layout.css` reserves it at the foot
 * of `.cib-shell`, so the last thing on a page - the Approve and Reject of a
 * refund case, the Danger zone of a record - stops above the strip instead of
 * under it.
 *
 * MEASURED, not written down: the strip wraps onto a second and third row on a
 * phone, and a scenario picker per live order makes it longer still, so any
 * fixed allowance is right for one width and short for the next. The 56px that
 * internal-admin used to reserve on `body` was also on the wrong box - a fill
 * screen's shell is exactly one viewport and scrolls inside itself, so padding
 * the body only added a second scrollbar and left the screen's own end under
 * the strip. Unset, the property falls back to `0px`, which is what production
 * (no strip) always reads.
 */
export const REHEARSAL_HEIGHT_PROPERTY = '--cib-rehearsal-height';

/**
 * Publishes the strip's rendered height on `<html>` and keeps it current while
 * the strip resizes; withdraws it when the strip leaves. A ref callback rather
 * than an effect because the strip renders nothing until its first poll
 * answers, so the node arrives later than the component mounts.
 */
function publishHeight(node: HTMLDivElement | null): (() => void) | undefined {
  if (node === null) return undefined;
  const root = node.ownerDocument.documentElement;
  const write = (): void => {
    root.style.setProperty(
      REHEARSAL_HEIGHT_PROPERTY,
      `${Math.ceil(node.getBoundingClientRect().height)}px`,
    );
  };
  write();
  const Observer = node.ownerDocument.defaultView?.ResizeObserver;
  const observer = Observer === undefined ? null : new Observer(write);
  observer?.observe(node);
  return () => {
    observer?.disconnect();
    root.style.removeProperty(REHEARSAL_HEIGHT_PROPERTY);
  };
}

export interface RehearsalBarProps {
  /**
   * The base the host app already uses for its own API calls - `''` where the
   * dev server proxies same-origin, `/api` for the guest app's prefixed proxy,
   * an absolute origin for a build pointed at a deployed API.
   */
  readonly apiBaseUrl: string;
  /**
   * Called after any press, and whenever the platform's offset moves under the
   * bar - which is what happens when ANOTHER tab presses something.
   *
   * The host decides what to refetch. It has to be a filtered
   * `invalidateQueries`: a blanket one fails `scripts/check-write-refetch.mjs`,
   * and it would also be wrong here - the bar knows the platform moved, not
   * which of the host's screens cares.
   */
  readonly onPlatformTimeChanged?: (() => void) | undefined;
}

export function RehearsalBar({
  apiBaseUrl,
  onPlatformTimeChanged,
}: RehearsalBarProps): JSX.Element | null {
  const [snapshot, setSnapshot] = useState<RehearsalSnapshot | null>(null);
  const [absent, setAbsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [scenarioByOrder, setScenarioByOrder] = useState<Record<string, string>>({});

  /** Presses this bar has already sent, so one tab never sends one twice. */
  const sent = useRef<Set<string>>(new Set());
  /** The offset at the previous poll: a change is another tab having pressed. */
  const lastOffset = useRef<number | null>(null);
  /** The live snapshot, for the Play timer - which must not re-arm on a poll. */
  const latest = useRef<RehearsalSnapshot | null>(null);
  latest.current = snapshot;
  const notify = useRef<(() => void) | undefined>(onPlatformTimeChanged);
  notify.current = onPlatformTimeChanged;

  const read = useCallback(async (): Promise<void> => {
    try {
      const response = await fetch(`${apiBaseUrl}/dev/rehearsal`, {
        headers: { accept: 'application/json' },
      });
      /*
       * Two ways this endpoint can be missing, and both mean the same thing.
       * A 404 is the gate refusing (production, or DEV_AUTH unset). An HTML
       * body is a dev server answering its SPA fallback because nothing proxies
       * `/dev` - which would otherwise have this bar parse an index.html as
       * JSON on a two-second loop.
       */
      if (response.status === 404) {
        setAbsent(true);
        return;
      }
      if (!response.ok) return;
      if (!(response.headers.get('content-type') ?? '').includes('json')) {
        setAbsent(true);
        return;
      }
      const body = (await response.json()) as RehearsalSnapshot;
      setSnapshot(body);
      if (lastOffset.current !== null && lastOffset.current !== body.clock.offsetMs) {
        notify.current?.();
      }
      lastOffset.current = body.clock.offsetMs;
    } catch {
      // A rehearsal aid must never be the reason a screen goes down. A failed
      // poll is simply the previous reading standing until the next one.
    }
  }, [apiBaseUrl]);

  const send = useCallback(
    async (path: string, body: unknown = {}): Promise<void> => {
      setBusy(true);
      try {
        await fetch(`${apiBaseUrl}${path}`, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(body),
        });
      } catch {
        // As above: a press that could not be delivered leaves the strip as it
        // was, and the next poll tells the truth about it.
      } finally {
        setBusy(false);
      }
      await read();
      notify.current?.();
    },
    [apiBaseUrl, read],
  );

  useEffect(() => {
    if (absent) return;
    void read();
    const timer = setInterval(() => void read(), POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [absent, read]);

  // The robo baker. Every press is claimed BEFORE it is sent, so the poll that
  // lands mid-flight cannot decide to send it again.
  useEffect(() => {
    const presses = roboBakerPresses(snapshot, sent.current);
    if (presses.length === 0) return;
    for (const press of presses) sent.current.add(press.key);
    void (async () => {
      for (const press of presses) {
        await send('/dev/actors/baker', { orderId: press.orderId, action: press.action });
      }
    })();
  }, [snapshot, send]);

  // Play: Step on a timer until the courier has nowhere left to go. The
  // snapshot is read from the ref rather than closed over, because a dependency
  // on it would re-arm this timer on every two-second poll and it would never
  // reach three seconds.
  useEffect(() => {
    if (!playing) return;
    const timer = setInterval(() => {
      if (!playContinues(latest.current)) {
        setPlaying(false);
        return;
      }
      void send('/dev/simulator/advance', { toNextEvent: true });
    }, PLAY_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [playing, send]);

  if (absent || snapshot === null) return null;

  const mock = snapshot.simulator !== null;
  const targets = scenarioTargets(snapshot);
  const scenarios = snapshot.simulator?.scenarios ?? [];

  return (
    <div className="cib-rehearsal" role="toolbar" aria-label="Rehearsal" ref={publishHeight}>
      <div className="cib-rehearsal__rail">
        <span className="cib-rehearsal__clock">{formatPlatformTime(snapshot.clock.now)}</span>
        <DataPill kind="rehearsal" value={formatOffset(snapshot.clock.offsetMs)} />
        <DataPill kind="rehearsal" value={snapshot.provider} />
        {mock ? (
          <DataPill kind="rehearsal" value={formatNextEvent(snapshot)} title="Next courier event" />
        ) : null}

        <Button size="sm" loading={busy} onClick={() => void send('/dev/clock/next-morning')}>
          Next morning
        </Button>
        <Button
          size="xs"
          variant="secondary"
          loading={busy}
          onClick={() => void send('/dev/rehearsal/advance', { minutes: 15 })}
        >
          +15 min
        </Button>
        <Button
          size="xs"
          variant="secondary"
          loading={busy}
          onClick={() => void send('/dev/rehearsal/advance', { minutes: 60 })}
        >
          +1 h
        </Button>
        {mock ? (
          <Button
            size="xs"
            variant="secondary"
            onClick={() => void send('/dev/simulator/advance', { toNextEvent: true })}
          >
            Step
          </Button>
        ) : null}
        {mock ? (
          <Button
            size="xs"
            variant={playing ? 'success' : 'secondary'}
            aria-pressed={playing}
            onClick={() => setPlaying((on) => !on)}
          >
            Play
          </Button>
        ) : null}
        <Button
          size="xs"
          variant="ghost"
          onClick={() => {
            sent.current.clear();
            setPlaying(false);
            void send('/dev/clock/reset');
          }}
        >
          Reset
        </Button>
        <Button
          size="sm"
          variant={snapshot.robo.baker ? 'success' : 'secondary'}
          aria-pressed={snapshot.robo.baker}
          onClick={() => {
            // Arming forgets what was pressed before: a rehearsal restarted
            // after a failed press must be able to reach the same order again.
            if (!snapshot.robo.baker) sent.current.clear();
            void send('/dev/actors/baker/auto', { enabled: !snapshot.robo.baker });
          }}
        >
          Robo baker
        </Button>

        {targets.map((order) => (
          <TableFilter
            key={order.orderId}
            label={orderHandle(order)}
            value={scenarioByOrder[order.orderId] ?? ''}
            onChange={(event) => {
              const scenario = event.target.value;
              setScenarioByOrder((chosen) => ({ ...chosen, [order.orderId]: scenario }));
              if (scenario !== '') {
                void send('/dev/simulator/scenario', { orderId: order.orderId, scenario });
              }
            }}
          >
            <option value="">Scenario</option>
            {scenarios.map((scenario) => (
              <option key={scenario} value={scenario}>
                {scenario}
              </option>
            ))}
          </TableFilter>
        ))}
      </div>
    </div>
  );
}
